import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Flame, Play, Check, Pause,
  Timer, Dumbbell, ChevronDown, ChevronUp, Youtube, ExternalLink,
  Maximize2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { fitnessApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import ActiveWorkoutMode from '../components/Workout/ActiveWorkoutMode';

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?]+)/);
  return match ? match[1] : null;
};

const WorkoutDetailPage = () => {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isActiveMode, setIsActiveMode] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(null);

  useEffect(() => {
    fetchWorkout();
  }, [workoutId]);

  const fetchWorkout = async () => {
    try {
      const data = await fitnessApi.getWorkout(workoutId);
      setWorkout(data);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: language === 'en' ? 'Failed to load workout' : 'Error al cargar el entrenamiento',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartWorkout = () => {
    setIsActiveMode(true);
  };

  const handleWorkoutComplete = async (data) => {
    try {
      await fitnessApi.completeWorkout(workoutId, data.duration_minutes, data.calories_burned);
      
      toast({
        title: language === 'en' ? 'Workout Complete!' : '¡Entrenamiento Completado!',
        description: language === 'en' 
          ? `Great job! You burned approximately ${data.calories_burned} calories`
          : `¡Excelente! Quemaste aproximadamente ${data.calories_burned} calorías`
      });
      
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      setIsActiveMode(false);
    }
  };

  const handleExitWorkout = () => {
    setIsActiveMode(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="min-h-screen bg-[#0d1f1f] flex items-center justify-center">
        <p className="text-gray-400">{language === 'en' ? 'Workout not found' : 'Entrenamiento no encontrado'}</p>
      </div>
    );
  }

  // Show ActiveWorkoutMode when workout is started
  if (isActiveMode) {
    return (
      <ActiveWorkoutMode
        workout={workout}
        language={language}
        onComplete={handleWorkoutComplete}
        onExit={handleExitWorkout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1f1f]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to={`/programs/${workout.program_id}`}>
                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                  <ArrowLeft className="w-6 h-6" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {language === 'en' ? workout.name_en : workout.name_es}
                </h1>
                <div className="flex items-center space-x-3 text-sm text-gray-400">
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {workout.duration_minutes} min
                  </span>
                  <span className="flex items-center">
                    <Flame className="w-4 h-4 mr-1" />
                    {workout.calories_estimate} kcal
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Pre-workout View - Exercise List */}
        <div className="space-y-6">
          <div className="bg-gray-900/50 rounded-3xl p-6 border border-gray-800">
            <p className="text-gray-300 mb-6">
              {language === 'en' ? workout.description_en : workout.description_es}
            </p>

            <Button
              onClick={handleStartWorkout}
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-lg font-semibold group"
              data-testid="start-workout-btn"
            >
              <Play className="w-6 h-6 mr-2" />
              {language === 'en' ? 'Start Workout' : 'Comenzar Entrenamiento'}
              <Maximize2 className="w-4 h-4 ml-2 opacity-60 group-hover:opacity-100" />
            </Button>
            <p className="text-center text-xs text-gray-500 mt-2">
              {language === 'en' ? 'Fullscreen mode with sounds' : 'Modo pantalla completa con sonidos'}
            </p>
          </div>

          {/* Exercise List */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4">
              {language === 'en' ? 'Exercises' : 'Ejercicios'} ({workout.exercises?.length || 0})
            </h2>

            <div className="space-y-3">
              {workout.exercises?.map((exercise, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden"
                  data-testid={`exercise-item-${index}`}
                >
                  <button
                    onClick={() => setExpandedExercise(expandedExercise === index ? null : index)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                        {index + 1}
                      </div>
                      <div className="text-left">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-white font-medium">
                            {language === 'en' ? exercise.name_en : exercise.name_es}
                          </h3>
                          {exercise.video_url && (
                            <Youtube className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {exercise.sets && `${exercise.sets} series`}
                          {exercise.reps && ` × ${exercise.reps} reps`}
                          {exercise.duration_seconds && `${exercise.duration_seconds}s`}
                          {exercise.rest_seconds && ` • ${exercise.rest_seconds}s descanso`}
                        </p>
                      </div>
                    </div>
                    {expandedExercise === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {expandedExercise === index && (
                    <div className="px-4 pb-4 space-y-4">
                      {/* Video or Image */}
                      {exercise.video_url ? (
                        <div className="aspect-video rounded-xl overflow-hidden bg-gray-800">
                          <iframe
                            src={`https://www.youtube.com/embed/${getYouTubeVideoId(exercise.video_url)}?rel=0`}
                            title={exercise.name_en}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : exercise.image_url && (
                        <div className="aspect-video rounded-xl overflow-hidden bg-gray-800">
                          <img 
                            src={exercise.image_url} 
                            alt={exercise.name_en}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <p className="text-gray-300 text-sm">
                        {language === 'en' ? exercise.description_en : exercise.description_es}
                      </p>
                      {exercise.video_url && (
                        <a 
                          href={exercise.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-red-400 hover:text-red-300"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          {language === 'en' ? 'Watch on YouTube' : 'Ver en YouTube'}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutDetailPage;
